import React, { useEffect, useState } from "react";
import { Form, Input, Radio, Button, Row, Col, Select, Breadcrumb, Divider, message, Modal } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useLocation, useParams } from "react-router-dom";
import styled from "styled-components";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { CALL_API } from "common/API";
import { useSelector } from "react-redux";
import { getUserInfo } from "redux/selectors";
import { countryList } from "common/Constants";

const { Option } = Select;

const PageWrap = styled.div`
  .client-page {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .ant-form-item {
    margin-bottom: 10px !important; 
  }

  .ant-form-item-label {
    padding-bottom: 4px !important;
  }

  .form-footer {
    position: sticky;
    bottom: 0;
    background: #fff;
    padding: 12px 16px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    border-top: 1px solid #eee;
    z-index: 10;
    .btn-add{
      background: #00685F !important;
      color: #ffffff !important;
      border: #00685F !important;
    }
    .cancel-btn{
      background: #ffffff !important;
      color: #000000 !important;
      border-color: #000000 !important;
    }
  }

  .ant-radio-checked .ant-radio-inner {
    border-color: #2ecc71 !important;
    background-color: #2ecc71 !important;
  }

  .ant-radio-inner::after {
    background-color: white !important; 
    border-color: #2ecc71 !important;
  }

  .ant-radio-wrapper:hover .ant-radio-inner {
    border-color: #2ecc71 !important;
  }
`;

const PersonCard = styled.div`
  background: #f5f5f5;
  border-radius: 8px;
  padding: 20px 20px 8px 20px;
  margin-bottom: 16px;
`;

const PersonCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  span {
    font-weight: 600;
    font-size: 14px;
  }
`;

const DeleteBtn = styled.div`
  cursor: pointer;
  background: #00685F !important;
  border-radius: 4px;
  padding: 6px 8px;
  display: flex;
  align-items: center;
  color: #ffffff;
  font-size: 16px;
  &:hover {
    opacity: 0.8;
  }
`;

const AddPersonBtn = styled(Button)`
  width: 320px;
  border-radius: 8px !important;
  background: #00685F !important;
  border-color: #00685F !important;
  color: #ffffff !important;
  font-weight: 500 !important;
  height: 40px !important;
  margin-top: 4px;
  display:flex;
  justify-content:center;
  align-items:center;
   &:hover {
      color: #ffffff !important;
  }
  &:focus {
      color: #ffffff !important;
  }
`;

const AddEditClient = () => {
    const [btnLoading, setBtnLoading] = useState(false);
    const [clientList, setClientList] = useState([]);
    const [form] = Form.useForm();
    const location = useLocation();
    const history = useHistory();
    const { id } = useParams();
    const userInfo = useSelector(getUserInfo);

    const editMode = location?.state?.editMode;
    const isPending = location?.state?.isPending;
    const isEdit = !!id;

    // Source of truth for prefilling the form. A reduced row object may be
    // passed via router state (e.g. when navigating from a filtered client
    // list, whose endpoint only returns a subset of fields), so whenever we
    // have a client id we re-fetch the full record by id and use that.
    const [clientData, setClientData] = useState(location?.state?.editData);

    // The edit route is "client-new" (no :id param defined in Routes), so
    // useParams() is empty here — derive the client id from the row passed
    // via router state to re-fetch the full record.
    const clientId = id || location?.state?.editData?.bdo_id;

    const clientType = Form.useWatch("clientType", form) ?? "company";

    useEffect(() => {
        fetchClients()
    }, [])

    useEffect(() => {
        if (clientId) fetchClientById(clientId);
    }, [clientId]);

    const fetchClientById = async (clientId) => {
        try {
            const res = await CALL_API(
                `/insyts-legal/getlegalclientbyid?clientid=${clientId}`,
                "get"
            );
            if (res?.code === "SUCCESS" && res?.data) {
                setClientData(res.data);
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        if (clientData) {
            setTimeout(() => {
                const rawType = (clientData.clienttype || clientData.clientType || "company").toLowerCase();
                const isCompany = rawType === "company";

                form.setFieldsValue({
                    clientType: rawType,

                    companyName: isCompany
                        ? (clientData.companyregisteredname || clientData.clientName)
                        : undefined,

                    clientname: !isCompany
                        ? (clientData.clientname || clientData.clientName)
                        : undefined,

                    owner: clientData.owner,
                    website: clientData.companywebsiteurl || clientData.website,
                    industry: clientData.primaryindustry || clientData.industry,
                    email: clientData.officialemail || clientData.email,
                    phone: clientData.phone,
                    primaryContact: clientData.primarycontactperson || clientData.primaryContact,
                    primaryEmail: clientData.primarycontactemail || clientData.primaryEmail,
                    registeredAddress: clientData.registeredaddress || clientData.registeredAddress,
                    city: clientData.city,
                    state: clientData.state,
                    zipcode: clientData.zipcode,
                    country: clientData.country || clientData.managingoffice,

                    status:
                        clientData.statusname === "active" || clientData.status === "active"
                            ? "Active"
                            : "Inactive",

                    personnel: Array.isArray(clientData.responsiblepersonnel)
                        ? clientData.responsiblepersonnel.map((p) => ({
                            personName: p.name || "",
                            designation: p.designation || "",
                            email: p.email || "",
                            phone: p.phone || "",
                        }))
                        : [],
                });
            }, 0);
        }
    }, [clientData]);

    const fetchClients = async () => {
        try {
            const res = await CALL_API(
                `/insyts-legal/getAllClientList`,
                "get"
            );
            if (res?.data) {
                setClientList(res.data);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const isDuplicateClient = (value) => {
        if (!value) return false;
        const input = value.trim().toLowerCase();
        return clientList?.some((c) => {
            const isSameRecord = c.bdo_id === (clientData?.bdo_id || clientId);
            if (isSameRecord) return false;
            if (clientType === "company") {
                return c.companyregisteredname?.toLowerCase() === input;
            }
            if (clientType === "person") {
                return c.clientname?.toLowerCase() === input;
            }
            return false;
        });
    };

    const onFinish = async (values) => {
        try {
            setBtnLoading(true);
            const isCompany = clientType === "company";

            const bdoId = clientData?.bdo_id || clientId;
            if (editMode && bdoId) {
                const payload = {
                    bdo_id: bdoId,
                    clienttype: clientType,

                    // company fields — empty string if person
                    companyregisteredname: isCompany ? (values.companyName || "") : "",
                    owner: isCompany ? (values.owner || "") : "",
                    createdby: userInfo?.userid || userInfo?.name || "",
                    primaryindustry: isCompany ? (values.industry || "") : "",
                    companywebsiteurl: isCompany ? (values.website || "") : "",

                    // person field — empty string if company
                    clientname: !isCompany ? (values.clientname || "") : "",

                    // shared fields
                    managingoffice: values.country || "",
                    officialemail: values.email || "",
                    phone: values.phone || "",
                    primarycontactperson: values.primaryContact || "",
                    primarycontactemail: values.primaryEmail || "",
                    registeredaddress: values.registeredAddress || "",
                    city: values.city || "",
                    state: values.state || "",
                    zipcode: values.zipcode || "",
                    country: values.country || "",
                    statusid: values.status === "Active" ? "1" : "0",
                    statusname: values.status?.toLowerCase(),

                    // personnel — only for company, else empty array
                    responsiblepersonnel: isCompany
                        ? (values.personnel || []).map((p) => ({
                            name: p?.personName || "",
                            designation: p?.designation || "",
                            email: p?.email || "",
                            phone: p?.phone || "",
                        }))
                        : [],
                };

                const response = await CALL_API(
                    "/insyts-legal/updatelegalclient",
                    "patch",
                    payload
                );

                if (response?.code === "SUCCESS") {
                    message.success("Client updated successfully");
                    history.push({ pathname: "/client", state: { refresh: true } });
                } else {
                    message.error("Update failed");
                }

            } else {
                const payload = {
                    clienttype: clientType,

                    // company fields — empty string if person
                    companyregisteredname: isCompany ? (values.companyName || "") : "",
                    owner: isCompany ? (values.owner || "") : "",
                    primaryindustry: isCompany ? (values.industry || "") : "",
                    companywebsiteurl: isCompany ? (values.website || "") : "",

                    // person field — empty string if company
                    clientname: !isCompany ? (values.clientname || "") : "",

                    // shared fields
                    managingoffice: values.country || "",
                    officialemail: values.email || "",
                    phone: values.phone || "",
                    primarycontactperson: values.primaryContact || "",
                    primarycontactemail: values.primaryEmail || "",
                    registeredaddress: values.registeredAddress || "",
                    city: values.city || "",
                    state: values.state || "",
                    zipcode: values.zipcode || "",
                    country: values.country || "",
                    statusid: values.status === "Active" ? "1" : "0",
                    statusname: values.status?.toLowerCase(),
                    createdby: userInfo?.userid || userInfo?.name || "",
                    createdbyname: userInfo?.name || userInfo?.userid || "",
                    // personnel — only for company, else empty array
                    responsiblepersonnel: isCompany
                        ? (values.personnel || []).map((p) => ({
                            name: p?.personName || "",
                            designation: p?.designation || "",
                            email: p?.email || "",
                            phone: p?.phone || "",
                        }))
                        : [],
                };

                const response = await CALL_API(
                    "/insyts-legal/addlegalclient",
                    "post",
                    payload
                );

                if (response?.code === "SUCCESS") {
                    message.success("Client created successfully");
                    history.push({ pathname: "/client", state: { refresh: true } });
                } else {
                    message.error("Create failed");
                }
            }

        } catch (error) {
            console.error(error);
            message.error("API error");
        } finally {
            setBtnLoading(false);
        }
    };

    // const handleTypeChange = (e) => {
    //     if (editMode) return;

    //     const newType = e.target.value;
    //     if (newType === "company") {
    //         form.resetFields([]);
    //     } else {
    //         form.resetFields([]);
    //     }
    // };
    const handleTypeChange = (e) => {
        if (editMode) return;
        const newType = e.target.value;
        form.resetFields();
        form.setFieldsValue({
            clientType: newType,
            country: "",
            status: "Active",
            personnel: [{ personName: "", designation: "", email: "", phone: "" }],
        });
    };

    return (
        <PageWrap>
            <div className="client-page">
                <div style={{ marginBottom: 12 }}>
                    <Breadcrumb>
                        <Breadcrumb.Item
                            onClick={() => history.push("/client")}
                            style={{ cursor: "pointer", color: "#1677ff" }}
                        >
                            Client
                        </Breadcrumb.Item>
                        {isPending && (
                            <Breadcrumb.Item
                                onClick={() => history.push("/pending-client")}
                                style={{ cursor: "pointer", color: "#1677ff" }}
                            >
                                Pending Client
                            </Breadcrumb.Item>
                        )}
                        <Breadcrumb.Item>
                            {isPending ? "Adding Pending Client" : editMode ? "Editing Client" : "Adding New Client"}
                        </Breadcrumb.Item>
                    </Breadcrumb>
                </div>

                <h4 style={{ marginTop: 8 }}>
                    General Information
                </h4>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{
                        clientType: "company",
                        country: "",
                        status: "Active",
                        personnel: [{ personName: "", designation: "", email: "", phone: "" }],
                    }}
                >
                    {/* ── Client Type ── */}
                    <Form.Item name="clientType" label="Client Type" required>
                        <Radio.Group onChange={handleTypeChange}>
                            <Radio value="company">Company</Radio>
                            <Radio value="person">Person</Radio>
                        </Radio.Group>
                    </Form.Item>

                    <Divider style={{ margin: "12px 0", borderColor: "#dadada" }} />

                    {/* ── COMPANY ── */}
                    {clientType === "company" && (
                        <>
                            <Row gutter={12}>
                                <Col span={12}>
                                    <Form.Item
                                        name="companyName"
                                        label="Company's Registered Name(Client Name)"
                                        rules={[
                                            { required: true, message: "Required" },
                                            {
                                                validator: (_, value) => {
                                                    if (isDuplicateClient(value)) {
                                                        return Promise.reject(
                                                            new Error("Client already exists")
                                                        );
                                                    }
                                                    return Promise.resolve();
                                                },
                                            },
                                        ]}
                                    >
                                        <Input
                                            onChange={(e) => {
                                                form.validateFields(["companyName"]);
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="owner"
                                        label="Owner"
                                        rules={[{ required: true, message: "Required" }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={12}>
                                <Col span={12}>
                                    <Form.Item
                                        name="email"
                                        label="Official Email to contact"
                                        rules={[{ required: true, message: "Required" }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="phone"
                                        label="Phone"
                                        rules={[
                                            { required: true, message: "Required" },

                                        ]}

                                    >
                                        <Input
                                            type="number"
                                            onKeyDown={(e) => {
                                                if (["e", "E", "+", "-", "."].includes(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            onPaste={(e) => {
                                                const paste = e.clipboardData.getData("text");
                                                if (!/^\d+$/.test(paste)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={12}>
                                <Col span={12}>
                                    <Form.Item name="industry" label="Primary Industry">
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="website" label="Company Website URL">
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={12}>
                                <Col span={12}>
                                    <Form.Item rules={[{ required: true, message: "Required" }]} name="primaryContact" label="Primary Contact Person">
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item rules={[{ required: true, message: "Required" }]} name="primaryEmail" label="Primary Contact Email">
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* ── Responsible Personnel ── */}
                            <h5 style={{ marginBottom: 12, fontWeight: 600, fontSize: 18, marginTop: 40 }}>
                                Other Responsible Personnel in the Organization
                            </h5>

                            <Form.List name="personnel">
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map((field, index) => (
                                            <PersonCard key={field.key}>
                                                <PersonCardHeader>
                                                    <span>Person {index + 1}</span>

                                                    {fields.length > 1 && (
                                                        <DeleteBtn
                                                            onClick={() => {
                                                                Modal.confirm({
                                                                    content: "Are you sure you want to delete this person?",
                                                                    okText: "Yes",
                                                                    cancelText: "No",
                                                                    centered: true,
                                                                    icon: null,
                                                                    okButtonProps: {
                                                                        style: {
                                                                            background: "#00685F",
                                                                            borderColor: "#00685F",
                                                                            color: "#ffffff",
                                                                            borderRadius: 8,
                                                                        },
                                                                    },
                                                                    cancelButtonProps: {
                                                                        style: { borderRadius: 8 },
                                                                    },
                                                                    onOk: () => remove(field.name),
                                                                });
                                                            }}
                                                        >
                                                            <DeleteOutlined />
                                                        </DeleteBtn>
                                                    )}
                                                </PersonCardHeader>

                                                <Row gutter={12}>
                                                    <Col span={12}>
                                                        <Form.Item
                                                            {...field}
                                                            name={[field.name, "personName"]}
                                                            label="Name of the person"
                                                            rules={[{ required: true, message: 'Required' }]}

                                                        >
                                                            <Input placeholder="" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={12}>
                                                        <Form.Item
                                                            {...field}
                                                            name={[field.name, "designation"]}
                                                            label="Role / Designation"
                                                            rules={[{ required: true, message: 'Required' }]}

                                                        >
                                                            <Input placeholder="" />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>

                                                <Row gutter={12}>
                                                    <Col span={12}>
                                                        <Form.Item
                                                            {...field}
                                                            name={[field.name, "email"]}
                                                            label="Email"
                                                            rules={[{ required: true, message: 'Required' }]}

                                                        >
                                                            <Input placeholder="" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={12}>
                                                        <Form.Item
                                                            {...field}
                                                            name={[field.name, "phone"]}
                                                            label="Phone"
                                                            rules={[{ required: true, message: 'Required' }]}

                                                        >
                                                            <Input
                                                                type="number"
                                                                onKeyDown={(e) => {
                                                                    if (["e", "E", "+", "-", "."].includes(e.key)) {
                                                                        e.preventDefault();
                                                                    }
                                                                }}
                                                                onPaste={(e) => {
                                                                    const paste = e.clipboardData.getData("text");
                                                                    if (!/^\d+$/.test(paste)) {
                                                                        e.preventDefault();
                                                                    }
                                                                }}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                            </PersonCard>
                                        ))}

                                        <AddPersonBtn onClick={() => add()} icon={<PlusOutlined />}>
                                            Add person
                                        </AddPersonBtn>
                                    </>
                                )}
                            </Form.List>
                        </>
                    )}

                    {/* ── PERSON ── */}
                    {clientType === "person" && (
                        <>
                            <Form.Item
                                name="clientname"
                                label="Client Name"
                                rules={[
                                    { required: true, message: "Required" },
                                    {
                                        validator: (_, value) => {
                                            if (isDuplicateClient(value)) {
                                                return Promise.reject(
                                                    new Error("Client already exists")
                                                );
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input
                                    onChange={() => {
                                        form.validateFields(["clientname"]);
                                    }}
                                />
                            </Form.Item>

                            <Row gutter={12}>
                                <Col span={12}>
                                    <Form.Item
                                        name="email"
                                        label="Official Email to contact"
                                        rules={[{ required: true, message: "Required" }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="phone"
                                        label="Phone"
                                        rules={[{ required: true, message: "Required" }]}
                                    >
                                        <Input type="Number" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={12}>
                                <Col span={12}>
                                    <Form.Item rules={[{ required: true, message: "Required" }]} name="primaryContact" label="Primary Contact Person">
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item rules={[{ required: true, message: "Required" }]} name="primaryEmail" label="Primary Contact Email">
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}

                    {/* ── ADDRESS ── */}
                    <h5 style={{ marginBottom: 16, marginTop: 45, fontWeight: 600, fontSize: 15 }}>
                        Address
                    </h5>

                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="registeredAddress" label="Registered Address">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="city" label="City">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="state" label="State/Province">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="zipcode" label="Zip/Postal code">
                                <Input type="Number" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item rules={[{ required: true, message: "Required" }]} name="country" label="Country">
                                <Select placeholder="Select Country" showSearch allowClear>
                                    {countryList?.map((c) => (
                                        <Option key={c} value={c}>
                                            {c}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* ── STATUS ── */}
                    <h5 style={{ marginBottom: 16, marginTop: 45, fontWeight: 600, fontSize: 15 }}>
                        Status
                    </h5>

                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="status" label="Status" style={{ marginBottom: 30 }}>
                                <Select>
                                    <Option value="Active">Active</Option>
                                    <Option value="Inactive">Inactive</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* ── Footer ── */}
                    <div className="form-footer">
                        <Button className="cancel-btn" htmlType="button" onClick={() => history.push("/client")}>
                            Cancel
                        </Button>
                        <Button className="btn-add" htmlType="submit" loading={btnLoading}>
                            {isPending ? "Complete" : editMode ? "Update" : "Complete"}
                        </Button>
                    </div>
                </Form>
            </div>
        </PageWrap>
    );
};

export default AddEditClient;